import { useCallback, useEffect, useState } from "react";
import { http, toastApiError } from "../lib/api";
import { fmtDate, fmtTL } from "../lib/format";
import AmountInput, { parseTL } from "./AmountInput";
import { toast } from "../lib/notify";
import { Wrench, Plus, Trash2, Pencil, X, Save } from "lucide-react";

const KINDS = ["Bakım", "Arıza", "Onarım", "Kalibrasyon", "Diğer"];
const STATUS = ["Tamamlandı", "Devam Ediyor", "Beklemede"];
const TABLE_HEADERS = ["Tarih", "Tür", "Durum", "Açıklama", "Servis", "Maliyet", "Sonraki Bakım", ""];
const emptyRow = () => ({ kind: "Bakım", date: new Date().toISOString().slice(0, 10), description: "", cost: "", vendor: "", next_date: "", status: "Tamamlandı" });
const cls = "w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md bg-white focus:border-amber-500 outline-none";

const Lbl = ({ t, className = "", children }) => <label className={`block ${className}`}><span className="block text-[10px] text-slate-500 mb-0.5">{t}</span>{children}</label>;

function MaintenanceForm({ form, setForm, busy, onCancel, onSave }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 p-3 bg-white border border-amber-300 rounded-md" data-testid="asset-maint-form">
      <Lbl t="Tür"><select value={form.kind} onChange={set("kind")} data-testid="asset-maint-kind" className={cls}>{KINDS.map((k) => <option key={k}>{k}</option>)}</select></Lbl>
      <Lbl t="Tarih *"><input type="date" value={form.date} onChange={set("date")} data-testid="asset-maint-date" className={cls} /></Lbl>
      <Lbl t="Sonraki Bakım Tarihi"><input type="date" value={form.next_date} onChange={set("next_date")} data-testid="asset-maint-next" className={cls} /></Lbl>
      <Lbl t="Durum"><select value={form.status} onChange={set("status")} data-testid="asset-maint-status" className={cls}>{STATUS.map((k) => <option key={k}>{k}</option>)}</select></Lbl>
      <Lbl t="Açıklama" className="md:col-span-2"><input value={form.description} onChange={set("description")} data-testid="asset-maint-desc" className={cls} placeholder="Yapılan işlem / arıza tanımı" /></Lbl>
      <Lbl t="Servis / Firma"><input value={form.vendor} onChange={set("vendor")} data-testid="asset-maint-vendor" className={cls} /></Lbl>
      <Lbl t="Maliyet (TL)"><AmountInput value={form.cost} onChange={(v) => setForm({ ...form, cost: v })} testid="asset-maint-cost" className={`${cls} text-right`} /></Lbl>
      <div className="col-span-2 md:col-span-4 flex justify-end gap-2">
        <button type="button" onClick={onCancel} data-testid="asset-maint-cancel" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-300 bg-white text-xs font-semibold"><X className="w-3.5 h-3.5" /> Vazgeç</button>
        <button type="button" onClick={onSave} disabled={busy} data-testid="asset-maint-save" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-60"><Save className="w-3.5 h-3.5" /> Kaydet</button>
      </div>
    </div>
  );
}

function MaintenanceTable({ rows, isAdmin, onEdit, onRemove }) {
  if (rows.length === 0) return <div className="text-xs text-slate-500" data-testid="asset-maint-empty">Bakım / arıza kaydı yok.</div>;
  return (
    <table className="w-full tbl">
      <thead><tr>{TABLE_HEADERS.map((h) => <th key={h || "islem"} className="text-left px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((m) => (
          <tr key={m.id} className="border-t border-slate-200" data-testid={`asset-maint-row-${m.id}`}>
            <td className="px-2 py-1 text-xs whitespace-nowrap">{fmtDate(m.date)}</td>
            <td className="px-2 py-1 text-xs"><span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${m.kind === "Arıza" ? "bg-red-100 text-red-800" : "bg-slate-200 text-slate-700"}`}>{m.kind}</span></td>
            <td className="px-2 py-1 text-xs">{m.status}</td>
            <td className="px-2 py-1 text-xs">{m.description || "—"}</td>
            <td className="px-2 py-1 text-xs">{m.vendor || "—"}</td>
            <td className="px-2 py-1 text-xs text-right font-mono">{m.cost ? fmtTL(m.cost) : "—"}</td>
            <td className="px-2 py-1 text-xs whitespace-nowrap">{m.next_date ? fmtDate(m.next_date) : "—"}</td>
            <td className="px-2 py-1 text-right whitespace-nowrap">
              <button type="button" onClick={() => onEdit(m)} data-testid={`asset-maint-edit-${m.id}`} className="text-slate-600 hover:text-slate-900 mr-2"><Pencil className="w-3.5 h-3.5" /></button>
              {isAdmin && <button type="button" onClick={() => onRemove(m)} data-testid={`asset-maint-delete-${m.id}`} className="text-red-700 hover:text-red-900"><Trash2 className="w-3.5 h-3.5" /></button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// bakım kayıtları: yükleme + ekle/düzenle/sil
function useMaintenance(assetId, onChanged) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRow);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => http.get(`/fixed-assets/${assetId}/maintenance`).then((r) => setRows(r.data)).catch(() => {}), [assetId]);
  useEffect(() => { load(); }, [load]);
  const start = (m) => { setEditing(m ? m.id : "new"); setForm(m ? { ...m, cost: m.cost ? fmtTL(m.cost).replace(" TL", "") : "", next_date: m.next_date || "" } : emptyRow()); };
  const save = async () => {
    if (!form.date) return toast.error("Tarih zorunlu");
    setBusy(true);
    const body = { kind: form.kind, date: form.date, description: form.description, cost: parseTL(String(form.cost || "")) ?? 0, vendor: form.vendor, next_date: form.next_date || null, status: form.status };
    try {
      if (editing === "new") await http.post(`/fixed-assets/${assetId}/maintenance`, body); else await http.put(`/fixed-assets/${assetId}/maintenance/${editing}`, body);
      toast.success(form.next_date ? `Bakım kaydedildi — sonraki bakım ${fmtDate(form.next_date)} için hatırlatılacak` : "Bakım kaydedildi");
      setEditing(null); load(); onChanged?.();
    } catch (e) { toastApiError(e); } finally { setBusy(false); }
  };
  const remove = async (m) => { if (!window.confirm("Bakım kaydı silinsin mi?")) return; try { await http.delete(`/fixed-assets/${assetId}/maintenance/${m.id}`); load(); onChanged?.(); } catch (e) { toastApiError(e); } };
  return { rows, editing, setEditing, form, setForm, busy, start, save, remove };
}

function NextMaintenanceBadge({ rows }) {
  const next = rows.find((r) => r.next_date)?.next_date;
  if (!next) return null;
  const overdue = next < new Date().toISOString().slice(0, 10);
  return <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${overdue ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`} data-testid="asset-next-maintenance">Sonraki bakım: {fmtDate(next)}{overdue ? " — GECİKTİ" : ""}</span>;
}

export default function AssetMaintenance({ assetId, isAdmin, onChanged }) {
  const { rows, editing, setEditing, form, setForm, busy, start, save, remove } = useMaintenance(assetId, onChanged);
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="asset-maintenance">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-600 inline-flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> Bakım / Arıza Takibi
          <NextMaintenanceBadge rows={rows} />
        </div>
        {editing === null && <button type="button" onClick={() => start(null)} data-testid="asset-maint-add" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800"><Plus className="w-3.5 h-3.5" /> Bakım / Arıza Kaydı</button>}
      </div>
      {editing !== null && <MaintenanceForm form={form} setForm={setForm} busy={busy} onCancel={() => setEditing(null)} onSave={save} />}
      <MaintenanceTable rows={rows} isAdmin={isAdmin} onEdit={start} onRemove={remove} />
    </div>
  );
}
